import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import axios from 'axios';
import { useFormProtection } from '../hooks/useFormProtection';
import HoneypotField from './HoneypotField';
import RecaptchaNotice from './RecaptchaNotice';

const ContactPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: selectedPlan ? `Hi! I'm interested in the ${selectedPlan} plan. Please contact me to discuss the details.` : '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { honeypotProps, onInteraction, getProtectionPayload } = useFormProtection('contact_form');

  // Update message when selectedPlan changes
  useEffect(() => {
    if (selectedPlan && !formData.message) {
      setFormData(prev => ({
        ...prev,
        message: `Hi! I'm interested in the ${selectedPlan} plan. Please contact me to discuss the details.`
      }));
    }
  }, [selectedPlan]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const protection = await getProtectionPayload();

      const response = await axios.post('/api/contact/full', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        consent: formData.consent,
        ...protection
      });

      if (response.data.status === 'success') {
        navigate('/success');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Contact Us
            </h1>
            {selectedPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-blue-800 font-medium text-sm sm:text-base">
                  Great choice! You're interested in the <span className="font-bold">{selectedPlan}</span> plan.
                </p>
                <p className="text-blue-600 text-xs sm:text-sm mt-1">
                  Let's discuss how we can get you started with Practice Dashboard.
                </p>
              </div>
            )}
            <p className="text-base sm:text-lg text-gray-600">
              Let's explore your pain points and discuss how Practice Dashboard along with AI and 
              Automation can help you reduce headcount costs and improve workflows in your Firm.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} onFocus={onInteraction} className="space-y-4 sm:space-y-6">
            <HoneypotField {...honeypotProps} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First name*
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
                  placeholder="First name..."
                />
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last name*
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
                  placeholder="Last name..."
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
                placeholder="Email address..."
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[44px]"
                placeholder="Phone number..."
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Your question
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                required
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-vertical"
                placeholder="Your question..."
              />
            </div>
            
            <div className="flex items-start">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                required
                className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded flex-shrink-0"
              />
              <label htmlFor="consent" className="ml-2 text-sm text-gray-600">
                "I consent to be contacted using the details provided and agree to the use of 
                my data for communication and marketing purposes."
              </label>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors min-h-[44px]"
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </button>

            <RecaptchaNotice />
          </form>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                What is Practice Dashboard?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Practice Dashboard is an add-on for Thomson Reuters Practice CS that provides automated, executive-level dashboards for firm owners and partners. It eliminates manual reporting and gives you real-time insights into revenue, margins, realization, and staff utilization.
              </p>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Do I need additional software to use it?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Yes — because Practice Dashboard runs on Microsoft Power BI, your firm will need at least one Power BI Pro license ($12/user/month, billed directly by Microsoft). This allows you to securely view and interact with dashboards.
              </p>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Can the dashboards be customized?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Yes. Executive and Enterprise plans include built-in customization hours each month. Additional custom work is available at an extra cost. All custom dashboards remain part of your subscription.
              </p>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                What happens if my subscription ends?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Access to both standard and custom dashboards is paused. If you decide to come back, your dashboards can be reactivated — no need to start from scratch.
              </p>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                What kind of support is included?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                We guarantee accurate, up-to-date dashboards. If something breaks or data is wrong, we fix it at no cost.
              </p>
            </div>
            
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                How long does onboarding take?
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Most firms are up and running in 3–5 business days after connecting Practice CS.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ContactPage;